/**
 * Per-symbol metadata (precision, session model) fetched once from
 * `GET /api/symbols`. A plain module cache, not Redux: the values are
 * effectively static config, and every price-formatting site already
 * re-renders when the active symbol changes, so a synchronous getter is
 * enough. Until the fetch resolves, `digitsFor` returns the gold default
 * (3) — harmless, since the app boots on XAUUSD.
 */

export interface SymbolMeta {
  symbol: string;
  name: string;
  digits: number;
  session_type: string; // "forex_daily" | "crypto_24h"
  default_tf?: string; // timeframe the chart opens at on selection
  /** Coarsest bar this symbol's source can produce; "" or absent = no floor.
   *  GC is built from Databento hourly bars, so nothing below 1H exists. */
  min_timeframe?: string;
}

const DEFAULT_TF = "1H";

export interface SymbolsPayload {
  symbols: SymbolMeta[];
}

const DEFAULT_DIGITS = 3;

let META: Record<string, SymbolMeta> = {};

export function setSymbolMetaList(list: SymbolMeta[]): void {
  META = Object.fromEntries(list.map((m) => [m.symbol.toUpperCase(), m]));
}

/** Price precision (decimal places) for a symbol; 3 until metadata loads. */
export function digitsFor(symbol: string | null | undefined): number {
  if (!symbol) return DEFAULT_DIGITS;
  return META[symbol.toUpperCase()]?.digits ?? DEFAULT_DIGITS;
}

/** Min price increment = 10^-digits, for the lightweight-charts axis. */
export function minMoveFor(symbol: string | null | undefined): number {
  return Math.pow(10, -digitsFor(symbol));
}

/** Pip size (price per 1 pip) for a symbol — used by measurement tools to
 *  express a price distance in pips. Conventions: gold = 0.1 (1 pip = $0.10,
 *  matching the Fibonacci tool's pip label), silver = 0.01; everything else
 *  uses the fractional-pip rule (one pip is the second-to-last quoted decimal,
 *  10^-(digits-1)): EURUSD(5dp)→0.0001, GBPJPY(3dp)→0.01, BTC(2dp)→0.1. */
export function pipSizeFor(symbol: string | null | undefined): number {
  const s = (symbol ?? "").toUpperCase();
  if (s.startsWith("XAU")) return 0.1;
  if (s.startsWith("XAG")) return 0.01;
  return Math.pow(10, -(digitsFor(symbol) - 1));
}

/** "forex_daily" | "crypto_24h" — drives the chart's day-rollover model. */
export function sessionTypeFor(symbol: string | null | undefined): string {
  if (!symbol) return "forex_daily";
  return META[symbol.toUpperCase()]?.session_type ?? "forex_daily";
}

/** Timeframe the chart should open at when this symbol is selected. */
export function defaultTfFor(symbol: string | null | undefined): string {
  if (!symbol) return DEFAULT_TF;
  return META[symbol.toUpperCase()]?.default_tf ?? DEFAULT_TF;
}

/** Coarsest timeframe this symbol has data for ("" = no floor). */
export function minTimeframeFor(symbol: string | null | undefined): string {
  if (!symbol) return "";
  return META[symbol.toUpperCase()]?.min_timeframe ?? "";
}

export function allSymbols(): SymbolMeta[] {
  return Object.values(META);
}
